import React from 'react';
import styled from 'styled-components';
import { useFormContext, Controller, ControllerRenderProps, UseFormStateReturn, ControllerFieldState } from 'react-hook-form';
import { Input, Select, Switch } from '../../common';
import { PresetCreateRequest, OrderType, OrderSide, AmountType, MarginMode, PriceType, LimitPriceType, PriceReference } from '../../../types/preset';
import PriceTypeSelector from '../../common/PriceTypeSelector';

interface OrderParamsProps {
  // Keep any essential props not directly related to form fields, if any
  // For now, it seems empty is fine if Wizard handles everything via context
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 0.5rem 0;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const SideSelector = styled.div`
  display: flex;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  overflow: hidden;
`;

const SideButton = styled.button<{ $active: boolean; $side: 'buy' | 'sell' }>`
  flex: 1;
  padding: 0.625rem;
  border: none;
  background-color: ${({ $active, $side }) => 
    $active ? ($side === 'buy' ? '#10b981' : '#ef4444') : 'white'};
  color: ${({ $active }) => $active ? 'white' : '#4b5563'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ $active, $side }) => 
      $active ? ($side === 'buy' ? '#10b981' : '#ef4444') : '#f9fafb'};
  }
`;

const InfoText = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
`;

type ControllerRenderParams = {
  field: ControllerRenderProps<PresetCreateRequest, any>; 
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<PresetCreateRequest>; 
};

// Helper function to check if order type is a cancellation type
const isCancellationOrder = (orderType?: OrderType): boolean => {
  return !!orderType && ['cancel_all', 'cancel_bids', 'cancel_asks'].includes(orderType);
};

const OrderParams: React.FC<OrderParamsProps> = () => {
  const { control, watch, formState: { errors }, setValue } = useFormContext<PresetCreateRequest>();

  const watchedOrderType = watch('order_type');
  const watchedLimitPriceType = watch('limit_price_type');

  const orderTypeOptions = [
    { value: 'market', label: 'Market' },
    { value: 'limit', label: 'Limit' },
    { value: 'stop_market', label: 'Stop Market' },
    { value: 'stop_limit', label: 'Stop Limit' },
    { value: 'layered', label: 'Layered/Split' },
    // Add Cancellation types
    { value: 'cancel_all', label: 'Cancel All Orders' }, 
    { value: 'cancel_bids', label: 'Cancel All Bids' },
    { value: 'cancel_asks', label: 'Cancel All Asks' },
  ];
  
  const amountTypeOptions = [
    { value: 'percent', label: 'Percent (Balance/Position)' },
    { value: 'base', label: 'Base Currency' },
    { value: 'quote', label: 'Quote Currency' }
  ];
  
  const marginModeOptions = [
    { value: 'cross', label: 'Cross' },
    { value: 'isolated', label: 'Isolated' }
  ];

  const showLimitPrice = watchedOrderType === 'limit' || watchedOrderType === 'stop_limit';
  const showStopPrice = watchedOrderType === 'stop_market' || watchedOrderType === 'stop_limit';
  
  const isLimitPriceInputEnabled = watchedLimitPriceType === LimitPriceType.ABSOLUTE;

  // Determine if the current type is a cancellation order
  const isCancelTypeSelected = isCancellationOrder(watchedOrderType);

  React.useEffect(() => {
    if (watchedOrderType === 'limit' && !watchedLimitPriceType) {
      setValue('limit_price_type', LimitPriceType.ABSOLUTE, { shouldValidate: true });
    }
  }, [watchedOrderType, watchedLimitPriceType, setValue]);

  React.useEffect(() => {
    if (watchedLimitPriceType !== LimitPriceType.ABSOLUTE) {
      setValue('limit_price', null);
    }
  }, [watchedLimitPriceType, setValue]);

  // Initialize layered object when order type changes to layered
  React.useEffect(() => {
    if (watchedOrderType === 'layered') {
      setValue('layered', {
        parts: 2,
        step: 1,
        distribution_type: 'step_based',
        price_type: LimitPriceType.ABSOLUTE,
        price_offset: 0,
        price_reference: PriceReference.LAST,
        center_on_price: false,
        distribution_strategy: 'even'
      }, { shouldValidate: true });
    } else if (watchedOrderType !== 'layered') {
      // Clear layered settings when switching away from layered
      setValue('layered', null);
    }
  }, [watchedOrderType, setValue]);

  return (
    <StepContainer>
      <FormSection>
        <SectionTitle>Order Parameters</SectionTitle>
        
        {/* Order Type Selector - Always Visible */} 
        <InputGroup>
          <Label htmlFor="order_type">Order Type</Label>
          <Controller
            name="order_type"
            control={control}
            render={({ field }: ControllerRenderParams) => (
              <Select
                id="order_type"
                options={orderTypeOptions} // Use updated options
                {...field}
              />
            )}
          />
          {errors.order_type && <ErrorText>{errors.order_type.message}</ErrorText>}
          {isCancelTypeSelected && (
             <InfoText>This preset will cancel orders based on the selected type when triggered.</InfoText>
          )}
        </InputGroup>

        {/* Render other fields only if NOT a cancellation type */} 
        {!isCancelTypeSelected && (
          <>
            {/* Side Selector */} 
            <InputGroup>
              <Label>Side</Label>
              <Controller
                name="side"
                control={control}
                render={({ field }: ControllerRenderParams) => (
                  <SideSelector>
                    <SideButton type="button" $active={field.value === 'buy'} $side="buy" onClick={() => field.onChange('buy')} >Buy/Long</SideButton>
                    <SideButton type="button" $active={field.value === 'sell'} $side="sell" onClick={() => field.onChange('sell')} >Sell/Short</SideButton>
                  </SideSelector>
                )}
              />
              {errors.side && <ErrorText>{errors.side.message}</ErrorText>}
            </InputGroup>

            {/* Order Flags - Moved here for better UX flow */}
            <InputGroup>
              <Label>Order Flags</Label>
              <FormRow>
                <InputGroup>
                  <Label htmlFor="flags.reduce_only">Reduce Only</Label>
                  <Controller
                    name="flags.reduce_only"
                    control={control}
                    render={({ field: { onChange, value } }: ControllerRenderParams) => (
                      <Switch checked={!!value} onChange={onChange} />
                    )}
                  />
                  <InfoText>Ensures the order only reduces your position size.</InfoText>
                </InputGroup>
                {watchedOrderType !== 'market' && (
                   <InputGroup>
                    <Label htmlFor="flags.post_only">Post Only</Label>
                    <Controller
                      name="flags.post_only"
                      control={control}
                      render={({ field: { onChange, value } }: ControllerRenderParams) => (
                        <Switch checked={!!value} onChange={onChange} />
                      )}
                    />
                     <InfoText>Ensures the order is only added to the order book (maker).</InfoText>
                  </InputGroup>
                )}
              </FormRow>
            </InputGroup>
            
            {/* Amount Row */} 
            <FormRow>
              <InputGroup>
                <Label htmlFor="amount">Amount</Label>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }: ControllerRenderParams) => (
                    <Input id="amount" type="number" placeholder="Enter amount" {...field} />
                  )}
                />
                {errors.amount && <ErrorText>{errors.amount.message}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <Label htmlFor="amount_type">Amount Type</Label>
                <Controller
                  name="amount_type"
                  control={control}
                  render={({ field }: ControllerRenderParams) => (
                    <Select id="amount_type" options={amountTypeOptions} {...field} />
                  )}
                />
                {errors.amount_type && <ErrorText>{errors.amount_type.message}</ErrorText>}
              </InputGroup>
            </FormRow>
            
            {/* Limit Price Section */} 
            {showLimitPrice && (
              <InputGroup>
                <Controller
                  name="limit_price_type"
                  control={control}
                  defaultValue={LimitPriceType.ABSOLUTE}
                  render={({ field: limitPriceTypeField }: ControllerRenderParams) => (
                    <Controller
                      name="price_offset"
                      control={control}
                      render={({ field: priceOffsetField }: ControllerRenderParams) => (
                        <Controller
                          name="price_reference"
                          control={control}
                          render={({ field: priceReferenceField }: ControllerRenderParams) => (
                            <PriceTypeSelector
                              priceType={limitPriceTypeField.value || LimitPriceType.ABSOLUTE}
                              onPriceTypeChange={limitPriceTypeField.onChange}
                              priceOffset={priceOffsetField.value}
                              onPriceOffsetChange={priceOffsetField.onChange}
                              priceReference={priceReferenceField.value || PriceReference.LAST}
                              onPriceReferenceChange={priceReferenceField.onChange}
                              label="Limit Price Type"
                            />
                          )}
                        />
                      )}
                    />
                  )}
                />
                {errors.limit_price_type && <ErrorText>{errors.limit_price_type.message}</ErrorText>}
                
                {/* Limit Price Input - only show for ABSOLUTE type */}
                {isLimitPriceInputEnabled && (
                  <InputGroup style={{ marginTop: '12px' }}>
                    <Label htmlFor="limit_price">Limit Price</Label>
                    <Controller
                      name="limit_price"
                      control={control}
                      render={({ field }: ControllerRenderParams) => (
                        <Input
                          id="limit_price"
                          type="number"
                          step="0.01"
                          placeholder="Enter limit price"
                          {...field}
                        />
                      )}
                    />
                    {errors.limit_price && <ErrorText>{errors.limit_price.message}</ErrorText>}
                  </InputGroup>
                )}
              </InputGroup>
            )}
            
            {/* Stop Price Input */} 
            {showStopPrice && (
              <InputGroup>
                <Label htmlFor="stop_price">Stop Price</Label>
                <Controller
                  name="stop_price"
                  control={control}
                  render={({ field }: ControllerRenderParams) => (
                    <Input id="stop_price" type="number" placeholder="Enter stop trigger price" {...field} />
                  )}
                />
                {errors.stop_price && <ErrorText>{errors.stop_price.message}</ErrorText>}
              </InputGroup>
            )}
          </> // End of conditional rendering for non-cancel types
        )}
      </FormSection>
      
      {/* Render Margin and Flags only if NOT a cancellation type */} 
      {!isCancelTypeSelected && (
        <>
          <FormSection>
            <SectionTitle>Margin Settings</SectionTitle>
            <FormRow>
              <InputGroup>
                <Label htmlFor="leverage">Leverage</Label>
                <Controller
                  name="leverage"
                  control={control}
                  render={({ field }: ControllerRenderParams) => (
                    <Input id="leverage" type="number" min="1" max="50" placeholder="e.g., 10" {...field} />
                  )}
                />
                {errors.leverage && <ErrorText>{errors.leverage.message}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <Label htmlFor="margin_mode">Margin Mode</Label>
                <Controller
                  name="margin_mode"
                  control={control}
                  render={({ field }: ControllerRenderParams) => (
                    <Select id="margin_mode" options={marginModeOptions} {...field} />
                  )}
                />
                {errors.margin_mode && <ErrorText>{errors.margin_mode.message}</ErrorText>}
              </InputGroup>
            </FormRow>
          </FormSection>

          {/* Removed old Flags section - now positioned after side selector for better UX flow */}
        </> // End of conditional rendering for non-cancel types
      )}

    </StepContainer>
  );
};

export default OrderParams; 